import api from "@/lib/api";
import { STORY_CACHE } from "@/lib/keptStories";

// Jeux gardés sur la tablette et parties jouées sans internet, envoyées au suivi plus tard.
const GAMES_KEY = "passerelle-kept-games";
const QUEUE_KEY = "passerelle-pending-sessions";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const read = (k) => { try { return JSON.parse(localStorage.getItem(k) || "[]"); } catch { return []; } };
const write = (k, v) => localStorage.setItem(k, JSON.stringify(v));
const gameKey = (item) => `${item.kind}-${item.id}`;

export const getKeptGames = () => read(GAMES_KEY);
export const isGameKept = (item) => getKeptGames().some((g) => gameKey(g) === gameKey(item));

function mediaUrls(obj, out = new Set()) {
  if (typeof obj === "string") {
    if (obj.startsWith("/api/files/")) out.add(`${BACKEND}${obj}`);
    else if (/^https?:\/\/\S+$/.test(obj)) out.add(obj);
  } else if (obj && typeof obj === "object") Object.values(obj).forEach((v) => mediaUrls(v, out));
  return [...out];
}

async function cacheMedia(urls) {
  if (!("caches" in window) || !urls.length) return;
  const cache = await caches.open(STORY_CACHE);
  await Promise.all(urls.map(async (u) => {
    try {
      const same = u.startsWith(window.location.origin) || u.startsWith(BACKEND);
      const res = await fetch(u, same ? { credentials: "include" } : { mode: "no-cors" });
      if (res.ok || res.type === "opaque") await cache.put(u, res);
    } catch { /* média non disponible */ }
  }));
}

const bravoKey = (gid) => `passerelle-bravo-${gid}`;

// Garde 3 félicitations du Gardien (texte + voix) pour le féliciter sans internet.
export async function keepBravo(gid) {
  if (!gid || !navigator.onLine) return;
  const got = await Promise.all([0, 1, 2].map(() => api.get(`/gardiens/${gid}/congrats`).then((r) => r.data).catch(() => null)));
  const list = got.filter(Boolean).filter((m, i, a) => a.findIndex((x) => x.text === m.text) === i);
  if (!list.length) return;
  await cacheMedia(list.flatMap((m) => [m.bravo_url, m.audio_url]).filter(Boolean).map((u) => `${BACKEND}${u}`));
  localStorage.setItem(bravoKey(gid), JSON.stringify(list));
}

export function cachedBravo(gid) {
  const list = read(bravoKey(gid));
  return list.length ? list[Math.floor(Math.random() * list.length)] : null;
}

export async function keepGame(item) {
  const urls = mediaUrls(item.game || {});
  await Promise.all([cacheMedia(urls), keepBravo(item.gardien_id)]);
  write(GAMES_KEY, [{ ...item, kept_media: urls }, ...getKeptGames().filter((g) => gameKey(g) !== gameKey(item))]);
}

export async function forgetGame(item) {
  const kept = getKeptGames().find((g) => gameKey(g) === gameKey(item));
  write(GAMES_KEY, getKeptGames().filter((g) => gameKey(g) !== gameKey(item)));
  if (kept?.kept_media?.length && "caches" in window) {
    const cache = await caches.open(STORY_CACHE);
    await Promise.all(kept.kept_media.map((u) => cache.delete(u)));
  }
}

export const pendingCount = () => read(QUEUE_KEY).length;

export function queueSession(payload) {
  write(QUEUE_KEY, [...read(QUEUE_KEY), { ...payload, played_at: new Date().toISOString() }]);
}

let flushing = false;
export async function flushSessions() {
  if (flushing || !navigator.onLine) return 0;
  flushing = true;
  let sent = 0;
  try {
    for (const s of read(QUEUE_KEY)) {
      try {
        await api.post("/sessions", s);
        sent += 1;
      } catch (e) {
        const st = e.response?.status;
        if (!st || st === 401 || st === 402 || st === 403 || st >= 500) break;
      }
      write(QUEUE_KEY, read(QUEUE_KEY).slice(1));
    }
  } finally { flushing = false; }
  return sent;
}

export const cachePatient = (id, data) => localStorage.setItem(`passerelle-patient-${id}`, JSON.stringify(data));
export const cachedPatient = (id) => { try { return JSON.parse(localStorage.getItem(`passerelle-patient-${id}`)); } catch { return null; } };
